import * as React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    Stack,
    Typography,
    useTheme,
    IconButton,
    Divider,
    useMediaQuery,
} from "@mui/material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { LoadingButton } from "@mui/lab";
import BaseModal from "./global/BaseModal";
import { CardPreferences } from "../services/cardPreferences.service";

// Dashboard Card IDs
export const DASHBOARD_CARD_IDS = {
    balance: "balance",
    jobCount: "jobCount",
    todaysProjects: "todaysProjects",
    approvalStatus: "approvalStatus",
    chartMetricsTitle: "chartMetricsTitle",
    totalHours: "totalHours",
    totalCost: "totalCost",
} as const;

export const DASHBOARD_CARD_LABELS: Record<string, string> = {
    [DASHBOARD_CARD_IDS.balance]: "Balance Card",
    [DASHBOARD_CARD_IDS.jobCount]: "Job Count",
    [DASHBOARD_CARD_IDS.todaysProjects]: "Work Management Quick View",
    [DASHBOARD_CARD_IDS.approvalStatus]: "Approval and Completion Statuses",
    [DASHBOARD_CARD_IDS.chartMetricsTitle]: "Chart Metrics Title",
    [DASHBOARD_CARD_IDS.totalHours]: "Total Hours Chart",
    [DASHBOARD_CARD_IDS.totalCost]: "Total Cost Chart",
};

// Default order for dashboard (matching the original layout order)
export const DEFAULT_DASHBOARD_CARD_ORDER = [
    DASHBOARD_CARD_IDS.balance,
    DASHBOARD_CARD_IDS.jobCount,
    DASHBOARD_CARD_IDS.todaysProjects,
    DASHBOARD_CARD_IDS.approvalStatus,
    DASHBOARD_CARD_IDS.chartMetricsTitle,
    DASHBOARD_CARD_IDS.totalHours,
    DASHBOARD_CARD_IDS.totalCost,
];

export const JOB_COUNT_CARD_IDS = {
    openJobs: "openJobs",
    scheduled: "scheduled",
    inProgress: "inProgress",
    completed: "completed",
    canceled: "canceled",
} as const;

export const JOB_COUNT_CARD_LABELS: Record<string, string> = {
    [JOB_COUNT_CARD_IDS.openJobs]: "Open Jobs",
    [JOB_COUNT_CARD_IDS.scheduled]: "Scheduled",
    [JOB_COUNT_CARD_IDS.inProgress]: "In-Progress",
    [JOB_COUNT_CARD_IDS.completed]: "Completed",
    [JOB_COUNT_CARD_IDS.canceled]: "Canceled",
};

export const DEFAULT_JOB_COUNT_ORDER = [
    JOB_COUNT_CARD_IDS.openJobs,
    JOB_COUNT_CARD_IDS.scheduled,
    JOB_COUNT_CARD_IDS.inProgress,
    JOB_COUNT_CARD_IDS.completed,
    JOB_COUNT_CARD_IDS.canceled,
];

interface SortableItemProps {
    id: string;
    label: string;
    isHidden: boolean;
    onToggleVisibility: (id: string) => void;
    showDetailButton?: boolean;
    onDetailClick?: (id: string) => void;
    isDetailExpanded?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
    id,
    label,
    isHidden,
    onToggleVisibility,
    showDetailButton = false,
    onDetailClick,
    isDetailExpanded = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                px: { xs: 1, sm: 1.5, md: 2 },
                mb: { xs: 0.75, sm: 1 },
                borderRadius: { xs: 1.5, sm: 2 },
                backgroundColor: isDragging
                    ? theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)"
                    : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.08)"
                    }`,
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                    borderColor: theme.palette.primary.main + "40",
                },
            }}
        >
            <IconButton
                {...attributes}
                {...listeners}
                size={isMobile ? "small" : "medium"}
                sx={{
                    cursor: "grab",
                    color: "text.secondary",
                    "&:active": {
                        cursor: "grabbing",
                    },
                    "&:hover": {
                        color: "primary.main",
                        backgroundColor: "primary.light" + "20",
                    },
                }}
            >
                <DragIndicatorIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <Typography
                sx={{
                    flex: 1,
                    fontWeight: isHidden ? 400 : 500,
                    opacity: isHidden ? 0.5 : 1,
                    color: isHidden ? "text.disabled" : "text.primary",
                    textDecoration: isHidden ? "line-through" : "none",
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                }}
            >
                {label}
            </Typography>
            {showDetailButton && onDetailClick && (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onDetailClick(id);
                    }}
                    size={isMobile ? "small" : "medium"}
                    sx={{ mr: { xs: 0.25, sm: 0.5 } }}
                >
                    {isDetailExpanded ? (
                        <ExpandLessIcon fontSize={isMobile ? "small" : "medium"} />
                    ) : (
                        <ExpandMoreIcon fontSize={isMobile ? "small" : "medium"} />
                    )}
                </IconButton>
            )}
            <IconButton
                onClick={() => onToggleVisibility(id)}
                size={isMobile ? "small" : "medium"}
                sx={{
                    color: isHidden ? "text.secondary" : "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light" + "20",
                    },
                }}
            >
                {isHidden ? (
                    <VisibilityOffIcon fontSize={isMobile ? "small" : "medium"} />
                ) : (
                    <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                )}
            </IconButton>
        </ListItem>
    );
};

interface DashboardCustomizeLayoutModalProps {
    open: boolean;
    onClose: () => void;
    preferences?: CardPreferences;
    onSave: (preferences: {
        cardOrder: string[];
        hiddenCards: string[];
        jobCountOrder: string[];
        hiddenJobCount: string[];
    }) => Promise<void>;
}

const DashboardCustomizeLayoutModal: React.FC<DashboardCustomizeLayoutModalProps> = ({
    open,
    onClose,
    preferences,
    onSave,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const [cardOrder, setCardOrder] = React.useState<string[]>([]);
    const [hiddenCards, setHiddenCards] = React.useState<string[]>([]);
    const [jobCountOrder, setJobCountOrder] = React.useState<string[]>(DEFAULT_JOB_COUNT_ORDER);
    const [hiddenJobCount, setHiddenJobCount] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);
    const [expandedJobCountDetail, setExpandedJobCountDetail] = React.useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Initialize state from preferences
    // Filter to only include valid dashboard card IDs
    React.useEffect(() => {
        if (preferences) {
            const allDashboardCardIds = Object.values(DASHBOARD_CARD_IDS);
            // If cardOrder is empty array or undefined, use default
            const savedOrder = (!preferences.cardOrder || preferences.cardOrder.length === 0)
                ? DEFAULT_DASHBOARD_CARD_ORDER
                : preferences.cardOrder;
            // Filter cardOrder to only include dashboard card IDs
            const filteredOrder = savedOrder.filter(
                (id) => allDashboardCardIds.includes(id as any)
            );
            // Add any missing cards from default order
            const missingCards = DEFAULT_DASHBOARD_CARD_ORDER.filter(
                (id) => !filteredOrder.includes(id)
            );
            const finalOrder = [...filteredOrder, ...missingCards];

            // Filter hiddenCards to only include dashboard card IDs
            const filteredHidden = (preferences.hiddenCards || []).filter(
                (id) => allDashboardCardIds.includes(id as any)
            );

            setCardOrder(finalOrder);
            setHiddenCards(filteredHidden);

            const allJobCountIds = Object.values(JOB_COUNT_CARD_IDS);
            // If jobCountOrder is empty array or undefined, use default
            // Check both legacy field and details.jobCount
            const savedJobCountOrder =
                (!preferences.jobCountOrder || preferences.jobCountOrder.length === 0)
                    ? (preferences.details?.jobCount?.jobCountOrder || DEFAULT_JOB_COUNT_ORDER)
                    : preferences.jobCountOrder;
            // If still empty, use default
            const finalSavedJobCountOrder = (!savedJobCountOrder || savedJobCountOrder.length === 0)
                ? DEFAULT_JOB_COUNT_ORDER
                : savedJobCountOrder;
            const filteredJobOrder = finalSavedJobCountOrder.filter(
                (id: string) => allJobCountIds.includes(id as any)
            );
            const missingJobSections = DEFAULT_JOB_COUNT_ORDER.filter(
                (id: string) => !filteredJobOrder.includes(id)
            );
            const finalJobOrder = [...filteredJobOrder, ...missingJobSections];

            // Check both legacy field and details.jobCount for hiddenJobCount
            const savedHiddenJobCount = preferences.hiddenJobCount || preferences.details?.jobCount?.hiddenJobCount || [];
            const filteredHiddenJob = savedHiddenJobCount.filter(
                (id: string) => allJobCountIds.includes(id as any)
            );

            setJobCountOrder(finalJobOrder);
            setHiddenJobCount(filteredHiddenJob);
        } else {
            setCardOrder(DEFAULT_DASHBOARD_CARD_ORDER);
            setHiddenCards([]);
            setJobCountOrder(DEFAULT_JOB_COUNT_ORDER);
            setHiddenJobCount([]);
        }
    }, [preferences, open]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setCardOrder((items) => {
                const oldIndex = items.indexOf(active.id as string);
                const newIndex = items.indexOf(over.id as string);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleToggleVisibility = (cardId: string) => {
        setHiddenCards((prev) =>
            prev.includes(cardId)
                ? prev.filter((id) => id !== cardId)
                : [...prev, cardId]
        );
    };

    const handleJobCountDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setJobCountOrder((items) => {
                const oldIndex = items.indexOf(active.id as string);
                const newIndex = items.indexOf(over.id as string);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleToggleJobCountVisibility = (sectionId: string) => {
        setHiddenJobCount((prev) =>
            prev.includes(sectionId)
                ? prev.filter((id) => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave({
                cardOrder,
                hiddenCards,
                jobCountOrder,
                hiddenJobCount,
            });
            onClose();
        } catch (error) {
            console.error("Failed to save preferences:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        setCardOrder(DEFAULT_DASHBOARD_CARD_ORDER);
        setHiddenCards([]);
        setJobCountOrder(DEFAULT_JOB_COUNT_ORDER);
        setHiddenJobCount([]);
        setExpandedJobCountDetail(false);
    };

    const handleJobCountDetailClick = (cardId: string) => {
        if (cardId === DASHBOARD_CARD_IDS.jobCount) {
            setExpandedJobCountDetail((prev) => !prev);
        }
    };

    const footer = (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 1.5 },
                width: "100%",
                justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
        >
            <Button
                onClick={handleReset}
                disabled={isSaving}
                fullWidth={isMobile}
                size={isMobile ? "medium" : "large"}
                sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    order: { xs: 3, sm: 1 },
                    mr: { xs: 0, sm: "auto" },
                }}
            >
                Reset to Default
            </Button>
            <Button
                onClick={onClose}
                disabled={isSaving}
                fullWidth={isMobile}
                size={isMobile ? "medium" : "large"}
                sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    order: { xs: 2, sm: 2 },
                }}
            >
                Cancel
            </Button>
            <LoadingButton
                onClick={handleSave}
                variant="contained"
                loading={isSaving}
                fullWidth={isMobile}
                size={isMobile ? "medium" : "large"}
                sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    order: { xs: 1, sm: 3 },
                }}
            >
                Save Changes
            </LoadingButton>
        </Box>
    );

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            title="Customize Dashboard Layout"
            footer={footer}
            maxWidth={isMobile ? "xs" : isTablet ? "sm" : "md"}
            useGlassCard={false}
        >
            <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: { xs: 2, sm: 2.5, md: 3 },
                        fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
                    }}
                >
                    Drag and drop cards to reorder them. Click the eye icon to show or
                    hide cards.
                </Typography>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={cardOrder}
                        strategy={verticalListSortingStrategy}
                    >
                        <List sx={{ p: 0 }}>
                            {cardOrder
                                .filter((cardId) => DASHBOARD_CARD_LABELS[cardId]) // Only show valid dashboard cards
                                .map((cardId, index, filteredArray) => (
                                    <React.Fragment key={cardId}>
                                        <SortableItem
                                            id={cardId}
                                            label={DASHBOARD_CARD_LABELS[cardId] || cardId}
                                            isHidden={hiddenCards.includes(cardId)}
                                            onToggleVisibility={handleToggleVisibility}
                                            showDetailButton={cardId === DASHBOARD_CARD_IDS.jobCount}
                                            onDetailClick={handleJobCountDetailClick}
                                            isDetailExpanded={expandedJobCountDetail && cardId === DASHBOARD_CARD_IDS.jobCount}
                                        />
                                        {cardId === DASHBOARD_CARD_IDS.jobCount && expandedJobCountDetail && (
                                            <Box
                                                sx={{
                                                    pl: { xs: 2, sm: 3, md: 4 },
                                                    pr: { xs: 1, sm: 1.5, md: 2 },
                                                    pt: { xs: 1.5, sm: 2 },
                                                    pb: { xs: 1.5, sm: 2 },
                                                }}
                                            >
                                                <Stack spacing={1} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                                                        }}
                                                    >
                                                        Job Count Sections
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" },
                                                        }}
                                                    >
                                                        Customize which job status metrics are shown inside the Job Count card.
                                                    </Typography>
                                                </Stack>

                                                <DndContext
                                                    sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={handleJobCountDragEnd}
                                                >
                                                    <SortableContext
                                                        items={jobCountOrder}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        <List sx={{ p: 0 }}>
                                                            {jobCountOrder
                                                                .filter((sectionId) => JOB_COUNT_CARD_LABELS[sectionId])
                                                                .map((sectionId, sectionIndex, sectionFilteredArray) => (
                                                                    <React.Fragment key={sectionId}>
                                                                        <SortableItem
                                                                            id={sectionId}
                                                                            label={JOB_COUNT_CARD_LABELS[sectionId] || sectionId}
                                                                            isHidden={hiddenJobCount.includes(sectionId)}
                                                                            onToggleVisibility={handleToggleJobCountVisibility}
                                                                        />
                                                                        {sectionIndex < sectionFilteredArray.length - 1 && (
                                                                            <Divider sx={{ mx: { xs: 1, sm: 1.5, md: 2 } }} />
                                                                        )}
                                                                    </React.Fragment>
                                                                ))}
                                                        </List>
                                                    </SortableContext>
                                                </DndContext>
                                            </Box>
                                        )}
                                        {index < filteredArray.length - 1 && (
                                            <Divider sx={{ mx: { xs: 1, sm: 1.5, md: 2 } }} />
                                        )}
                                    </React.Fragment>
                                ))}
                        </List>
                    </SortableContext>
                </DndContext>

                {/* {enableJobCountSections && (
                    <>
                        <Divider sx={{ my: 3 }} />

                        <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Job Count Sections
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Customize which job status metrics are shown inside the Job Count card.
                            </Typography>
                        </Stack>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleJobCountDragEnd}
                        >
                            <SortableContext
                                items={jobCountOrder}
                                strategy={verticalListSortingStrategy}
                            >
                                <List sx={{ p: 0 }}>
                                    {jobCountOrder
                                        .filter((sectionId) => JOB_COUNT_CARD_LABELS[sectionId])
                                        .map((sectionId, index, filteredArray) => (
                                            <React.Fragment key={sectionId}>
                                                <SortableItem
                                                    id={sectionId}
                                                    label={JOB_COUNT_CARD_LABELS[sectionId] || sectionId}
                                                    isHidden={hiddenJobCount.includes(sectionId)}
                                                    onToggleVisibility={handleToggleJobCountVisibility}
                                                />
                                                {index < filteredArray.length - 1 && <Divider sx={{ mx: 2 }} />}
                                            </React.Fragment>
                                        ))}
                                </List>
                            </SortableContext>
                        </DndContext>
                    </>
                )} */}
            </Box>
        </BaseModal>
    );
};

export default DashboardCustomizeLayoutModal;

